/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  Output,
  QueryList,
} from '@angular/core';
import { startWith$ } from '@gh/rx/operators';
import { TabDirective } from './tab.directive';

/**
 * Tab groups lets organize separate views where only one view can be visible at a time.
 * This component provides capabilities similar to mat-tab-group, but it has additional features which absent
 * in mat-tab-group (at least on the moment of writing).
 *
 * <ul>
 *   <li><b>State based management of focused tab.</b>
 *     For example, developer can catch tab selection event, run some logic and switch to requested tab only if it is necessary
 *   </li>
 *   <li><b>Tab names</b>
 *     Each tab should have unique name. It is important for manual navigation and more convenient comparing with <code>selectedIndex</code>
 *   </li>
 *   <li><b>Stacking tabs</b>
 *     Even when content should be hidden tab group renders it on the page (depending on rendering mode).
 *     Tab group puts hidden tab on the background (using z-index) and makes it invisible (using visiblity property).
 *     Such behaviour allows to solve some problems with rendering of grids.
 *   </li>
 *   <li><b>Different tab rendering modes</b>
 *     Tab rendering mode allows
 *       * to rerender content each time tab is opened (<code>always</code> mode)
 *       * to render content lazily on the first opening and do not rerender tab in future (<code>lazy</code> mode)
 *       * to render content immediately after tab group is created. Tab will be rendered event if it is not opened (<code>eager</code>)
 *   </li>
 * </ul>
 */
@Component({
  standalone: false,
  selector: 'gh-tab-group',
  templateUrl: 'tab-group.component.html',
  styleUrls: ['tab-group.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TabGroupComponent implements AfterContentInit {
  @Output() selectedChange = new EventEmitter<string>();

  @ContentChildren(TabDirective) tabs: QueryList<TabDirective>;

  activeTab?: TabDirective;

  private initialized = false;
  private _selected: string;

  @Input()
  set selected(selected: string) {
    this._selected = selected;

    if (this.initialized) {
      this.setActiveTab(this.tabs.find((tab) => tab.name === selected));
    }
  }

  constructor(private changeDetectorRef: ChangeDetectorRef) {

  }

  ngAfterContentInit(): void {
    this.tabs.changes.pipe(startWith$(void 0)).subscribe(() => {
      this.updateTabSet();
      this.updateActiveTab();
    });

    this.initialized = true;
  }

  onClick(tab: TabDirective): void {
    this.selectedChange.emit(tab.name);
  }

  isSelected(tab: TabDirective): boolean {
    return this.activeTab === tab;
  }

  shouldRender(tab: TabDirective): boolean {
    switch (tab.renderStrategy) {
      case 'always':
        return tab === this.activeTab;
      case 'eager':
        return true;
      default:
        return tab.renderedOnce;
    }
  }

  private updateTabSet(): void {
    this.tabs.forEach((tab) => tab.registerOnChange(this.onChildTabChanged.bind(this)));
  }

  private onChildTabChanged(): void {
    this.changeDetectorRef.markForCheck();
  }

  private updateActiveTab(): void {
    const { activeTab, _selected: selected } = this;
    if (activeTab && this.tabs.find((tab) => tab === activeTab)) {
      this.setActiveTab(activeTab);
    } else if (selected) {
      this.setActiveTab(this.tabs.find((tab) => tab.name === selected));
    } else {
      this.setActiveTab(this.tabs.first);
    }
  }

  private setActiveTab(tab?: TabDirective): void {
    const { activeTab } = this;

    if (tab !== activeTab && tab) {
      tab.setRenderedOnce();
    }

    this.activeTab = tab;

    this.changeDetectorRef.markForCheck();
  }
}
